import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listResource } from '@/lib/api';
import { roleActionErrorToast, roleDeleteSuccessToast } from '@/lib/user-feedback';
import { isSystemRole, hierarchyLevelLabel, roleHierarchyLevel, roleIcon, roleLabel, sortRolesByHierarchy } from '@/lib/role-display';

interface RoleRow {
  id: string;
  name: string;
  label?: string | null;
  hierarchyLevel?: number;
  permissionsCount?: number;
  usersCount?: number;
  _count?: { permissions?: number; users?: number };
}

const PER_PAGE = 10;

export function RolesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission, canManageRole } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.ROLES_CREAR);
  const canEdit = hasPermission(PERMISSIONS.ROLES_EDITAR);
  const canDelete = hasPermission(PERMISSIONS.ROLES_ELIMINAR);
  const [page, setPage] = useState(1);
  const [roleToDelete, setRoleToDelete] = useState<RoleRow | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['roles'],
    queryFn: () => listResource<RoleRow>('/roles'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setRoleToDelete(null);
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast(roleDeleteSuccessToast());
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el rol'));
    },
  });

  const allRoles = useMemo(() => sortRolesByHierarchy(data?.data ?? []), [data?.data]);
  const filteredTotal = allRoles.length;
  const totalPages = Math.max(1, Math.ceil(filteredTotal / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const firstItem = filteredTotal === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const lastItem = Math.min(currentPage * PER_PAGE, filteredTotal);

  const roles = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return allRoles.slice(start, start + PER_PAGE);
  }, [allRoles, currentPage]);

  function handleDelete(role: RoleRow) {
    const userCount = role._count?.users ?? role.usersCount ?? 0;
    const system = isSystemRole(role.name);

    if (system) {
      toast({ title: 'No se puede eliminar un rol del sistema.', variant: 'danger' });
      return;
    }
    if (userCount > 0) {
      toast({ title: 'No se puede eliminar un rol con usuarios asignados.', variant: 'danger' });
      return;
    }
    setRoleToDelete(role);
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=users" label="Volver a Administración" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Roles</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">Gestión de roles y permisos del sistema</p>
          </div>
        </div>
        {canCreate && (
          <Link
            to="/roles/create"
            className="inline-flex items-center gap-2 rounded-xl bg-dash-primary px-6 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] md:ml-14"
          >
            <span className="material-symbols-outlined text-xl" aria-hidden="true">
              add
            </span>
            Nuevo rol
          </Link>
        )}
      </div>

      {error && (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      <div className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : filteredTotal === 0 ? (
            <div className="lms-gradebook__table-wrap">
              <div className="lms-gradebook__empty">
                <div className="lms-gradebook__empty-icon" aria-hidden="true">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    shield_person
                  </span>
                </div>
                <h3>Sin roles</h3>
                <p>Aún no se han creado roles en el sistema.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="lms-gradebook__table-wrap">
                <table className="lms-gradebook__table">
                  <thead>
                    <tr>
                      <th className="lms-gradebook__th" scope="col">
                        Nivel
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Nombre
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Permisos
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Usuarios
                      </th>
                      <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map((role) => {
                      const label = roleLabel(role.name, role.label);
                      const icon = roleIcon(role.name);
                      const permCount = role._count?.permissions ?? role.permissionsCount ?? 0;
                      const userCount = role._count?.users ?? role.usersCount ?? 0;
                      const system = isSystemRole(role.name);
                      const level = role.hierarchyLevel ?? roleHierarchyLevel(role.name);
                      const manageable = canManageRole(role.name, role.hierarchyLevel);

                      return (
                        <tr key={role.id}>
                          <td className="lms-gradebook__td">
                            <Badge variant="primary" title={`Nivel jerárquico ${level}`}>
                              {hierarchyLevelLabel(level)}
                            </Badge>
                          </td>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              <div className="lms-gradebook__avatar" aria-hidden="true">
                                <span className="material-symbols-outlined text-[1.25rem]">{icon}</span>
                              </div>
                              <div className="min-w-0">
                                <Link to={`/roles/${role.id}`} className="lms-gradebook__name">
                                  {label}
                                </Link>
                                {system ? (
                                  <div className="lms-gradebook__muted">Sistema</div>
                                ) : label !== role.name ? (
                                  <div className="lms-gradebook__muted">{role.name}</div>
                                ) : null}
                              </div>
                            </div>
                          </th>
                          <td className="lms-gradebook__td" title={`${permCount} ${permCount === 1 ? 'permiso' : 'permisos'}`}>
                            {permCount}
                          </td>
                          <td className="lms-gradebook__td">{userCount}</td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/roles/${role.id}`}
                                className="lms-gradebook__action"
                                title="Ver"
                                aria-label={`Ver ${label}`}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  visibility
                                </span>
                              </Link>
                              {canEdit && manageable && (
                                <Link
                                  to={`/roles/${role.id}/edit`}
                                  className="lms-gradebook__action"
                                  title="Editar"
                                  aria-label={`Editar ${label}`}
                                >
                                  <span className="material-symbols-outlined" aria-hidden="true">
                                    edit
                                  </span>
                                </Link>
                              )}
                              {canDelete && manageable && (
                                <button
                                  type="button"
                                  className="lms-gradebook__action"
                                  title="Eliminar"
                                  aria-label={`Eliminar ${label}`}
                                  onClick={() => handleDelete(role)}
                                  disabled={deleteMutation.isPending}
                                >
                                  <span className="material-symbols-outlined" aria-hidden="true">
                                    delete
                                  </span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>{firstItem}</strong> a <strong>{lastItem}</strong> de{' '}
                  <strong>{filteredTotal}</strong> resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página anterior"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página siguiente"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <ConfirmModal
        open={roleToDelete !== null}
        title="Eliminar rol"
        description={
          roleToDelete
            ? `¿Está seguro de eliminar el rol «${roleLabel(roleToDelete.name, roleToDelete.label)}»?`
            : undefined
        }
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (roleToDelete) deleteMutation.mutate(roleToDelete.id);
        }}
        onClose={() => {
          if (!deleteMutation.isPending) setRoleToDelete(null);
        }}
      />
    </div>
  );
}
